import { z } from 'zod';
import { DateOnlySchema, IdSchema } from './common.js';
import { ManagerMonthStateSchema, MonthSchema, PortfolioPeriodSchema } from './reports.js';
// ---------------------------------------------------------------------------
// Move-ins and move-outs a park confirms each month
// ---------------------------------------------------------------------------
/** Spark 287230: managers confirm each month's moves, and that list is the data source. */
export const MoveDirectionSchema = z.enum(['in', 'out']);
export const ConfirmedMoveSchema = z.strictObject({
    id: IdSchema,
    direction: MoveDirectionSchema,
    tenantName: z.string(),
    lotNumber: z.string(),
    /** The date Rent Manager has, or the one entered; null when neither. */
    moveDate: DateOnlySchema.nullable(),
    /** Move-out: sold the park model to the next resident. Not counted as a move-out. */
    soldToNewResident: z.boolean(),
    /** Move-in: bought the park model from the resident leaving. Not counted as a move-in. */
    boughtFromResident: z.boolean(),
    /** Move-in: bought a park model from the park. Still counted. */
    boughtFromPark: z.boolean(),
    notes: z.string().nullable(),
    /** Move-in only, optional. */
    leadSource: z.string().nullable(),
    /** Filled from Rent Manager, rather than added by hand. */
    fromRentManager: z.boolean(),
    /** A filled row the manager removed. Kept so it is not filled again; not counted. */
    removed: z.boolean(),
    /** Whether it counts, by her rules: not removed, and not a sale between residents. */
    counts: z.boolean(),
});
export const MoveConfirmationMonthSchema = z.strictObject({
    month: MonthSchema,
    /** Whether the month has begun. Moves are filled and added from then. */
    begun: z.boolean(),
    /** Whether the month has ended. Only an ended month can be closed. */
    ended: z.boolean(),
    state: ManagerMonthStateSchema,
    entries: z.array(ConfirmedMoveSchema),
    /** What the entries count as. Reports use these once the month is closed. */
    moveIns: z.number().int().nonnegative(),
    moveOuts: z.number().int().nonnegative(),
    /** Rent Manager's own count: what reports show until the month is closed. */
    rentManagerMoveIns: z.number().int().nonnegative(),
    rentManagerMoveOuts: z.number().int().nonnegative(),
});
export const MoveConfirmationParkSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    months: z.array(MoveConfirmationMonthSchema),
});
/** A month or a quarter, as on the portfolio report. */
export const MoveConfirmationQuerySchema = z.strictObject({ period: PortfolioPeriodSchema });
export const MoveConfirmationResponseSchema = z.strictObject({
    period: PortfolioPeriodSchema,
    months: z.array(MonthSchema),
    /** The caller may reopen a closed month (`reports.reconcile`). */
    canReopen: z.boolean(),
    parks: z.array(MoveConfirmationParkSchema),
});
const tenantName = z.string().trim().min(1).max(200);
const lotNumber = z.string().trim().max(50);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));
/**
 * The fields a manager fills in. The boxes belong to one side each: a move-out
 * sells a park model, a move-in buys one and has a lead source. The API refuses
 * a box on the wrong side rather than dropping it silently.
 */
export const ConfirmedMoveFieldsSchema = z.strictObject({
    tenantName,
    lotNumber,
    moveDate: DateOnlySchema.nullable(),
    soldToNewResident: z.boolean(),
    boughtFromResident: z.boolean(),
    boughtFromPark: z.boolean(),
    notes: optionalText(2000),
    leadSource: optionalText(200),
});
export const CreateConfirmedMoveRequestSchema = ConfirmedMoveFieldsSchema.extend({
    parkId: IdSchema,
    month: MonthSchema,
    direction: MoveDirectionSchema,
});
/** Any subset of the fields. `removed: false` restores a move removed with DELETE. */
export const UpdateConfirmedMoveRequestSchema = ConfirmedMoveFieldsSchema.partial().extend({
    removed: z.literal(false).optional(),
});
